import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Routes, Route, useLocation } from 'react-router-dom'
import type { Organization, Definition } from './types'
import { DataContextProvider } from './components/LandingPage/contexts/DataContextProvider'
import MapTableContainer from './components/MapTableContainer'
import About from './components/About'
// import {
//   AddNewOrganization,
//   AddNewOrganizationMobile,
// } from './components/AddNewOrganization'
import { RoutePath } from './constants'
import { Intro, IntroMobile } from './components/Intro'
import { LandingPage } from './components/LandingPage'
import { useHash } from './components/IntroSection/hooks'
import { ascending } from 'd3'
//import NavBar from './components/NavBar'
import AddNewOrgWithFormLink from './components/AddNewOrganization/AddNewOrgWithFormLink'

const API_ENDPOINT = 'https://decolonizing-wealth-project-data.s3.amazonaws.com'
const DATA_URL =
  process.env.REACT_APP_VERSION?.toLowerCase() === 'dev'
    ? 'decolonizing-wealth-project-data.json'
    : 'decolonizing-wealth-project-data-production.json'
// const NODES = [<Intro />, <IntroSection />]

function App() {
  const location = useLocation()

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname,
      })
    }
  }, [location])

  const scrolledRef = useRef(false)
  const [hash] = useHash()
  const [explainerStep, setExplainerStep] = useState<number>(0)
  const handleExplainerStepChange = (step: number) => setExplainerStep(step)
  const [isClient, setIsClient] = useState(false)
  const [hasSeenInitialMapExplainer, setHasSeenInitialMapExplainer] =
    useState(false)
  const hideInitialMapExplainer = () => setHasSeenInitialMapExplainer(true)

  useEffect(() => {
    if (hash && !scrolledRef.current) {
      const id = hash.replace('#', '')
      const element = document.getElementById(id)
      if (element) {
        setTimeout(
          () => {
            element.scrollIntoView({ behavior: 'smooth' })
          },
          isClient ? 0 : 1000
        )
      }
    }

    setIsClient(true)
  }, [hash, isClient])

  const { isLoading, error, data } = useQuery<{
    organizations: Organization[]
    initiatives: Organization[]
    definitions: Definition[]
  }>({
    queryKey: ['organizations'],
    queryFn: () =>
      fetch(`${API_ENDPOINT}/${DATA_URL}`)
        .then((res) => res.json())
        .then((res) => {
          const x = JSON.parse(res)
          return {
            ...x,
            organizations: x.organizations.map((org: Organization) => {
              return {
                ...org,
                institution:
                  (org.target || []).filter((t: string) =>
                    t.toLowerCase().startsWith('institutions:')
                  ) || [],
                geo_scope: org.geo_scope.sort(ascending) || [],
              }
            }),
            initiatives: x.initiatives.map((org: Organization) => {
              return {
                ...org,
                institution:
                  (org.target || []).filter((t: string) =>
                    t.toLowerCase().startsWith('institutions:')
                  ) || [],
                geo_scope: org.geo_scope.sort(ascending) || [],
              }
            }),
          }
        }),
  })

  if (error) {
    console.error(error)
  }
  if (isLoading || !data) {
    return null
  }
  const { organizations = [], initiatives = [], definitions = [] } = data

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <DataContextProvider
              data={initiatives.concat(organizations)}
              definitions={definitions}
            >
              <div className="w-full h-full block ">
                <div className="hidden md:block">
                  <Intro />
                </div>
                <div className="md:hidden">
                  <IntroMobile />
                </div>
                <LandingPage />
              </div>
            </DataContextProvider>
          }
        />
        <Route
          path={RoutePath.Map}
          element={
            <MapTableContainer
              organizations={organizations}
              initiatives={initiatives}
              definitions={definitions}
              explainerStep={explainerStep}
              onExplainerStepChange={handleExplainerStepChange}
              hasSeenInitialMapExplainer={hasSeenInitialMapExplainer}
              hideInitialMapExplainer={hideInitialMapExplainer}
            />
          }
        />
        <Route
          path={RoutePath.Table}
          element={
            <MapTableContainer
              organizations={organizations}
              initiatives={initiatives}
              definitions={definitions}
              explainerStep={explainerStep}
              onExplainerStepChange={handleExplainerStepChange}
              hasSeenInitialMapExplainer={hasSeenInitialMapExplainer}
              hideInitialMapExplainer={hideInitialMapExplainer}
            />
          }
        />
        <Route path={RoutePath.About} element={<About />} />
        <Route
          path={RoutePath.AddOrganization}
          element={
            <div>
              <AddNewOrgWithFormLink />
            </div>
          }
        />
      </Routes>
    </>
  )
}

export default App
